import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { PhoneIcon, XIcon, CheckCircleIcon, UserIcon, SearchIcon, FilterIcon, HashIcon, EditIcon, BanIcon, GripVerticalIcon, ListIcon, HomeIcon, ClockIcon, MapPinIcon, EuroIcon, BuildingIcon, DoorOpenIcon, RefreshCwIcon, FileEditIcon } from 'lucide-react';
import { CallStatusModal } from '../../components/rental/CallStatusModal';
import { RescheduleCallModal } from '../../components/rental/RescheduleCallModal';
import { TransformLeadModal } from '../../components/rental/TransformLeadModal';
import { QualifiedLeadDetailsPanel } from '../../components/rental/QualifiedLeadDetailsPanel';
import { LeadDetailsPanel } from '../../components/rental/LeadDetailsPanel';
export interface ActionTimestamp {
  action: string;
  timestamp: string;
  agent: string;
}
export interface BuyerLead {
  leadId: string;
  createdAt: string;
  entryDate: string;
  entryTime: string;
  name: string;
  phone: string;
  propertyId?: string;
  callCenterMemo: string;
  callCompleted: boolean;
  callStatus: string;
  fullName?: string;
  propertyType?: string;
  rooms?: number;
  budgetMin?: number;
  budgetMax?: number;
  zones: string[];
  excludedFloors?: string;
  maxFloors?: number;
  bathrooms?: number;
  constructionYear?: number;
  qualificationMemo?: string;
  whoWillMove?: string;
  acquisitionReason?: string;
  qualified: boolean;
  actionHistory: ActionTimestamp[];
}
type KanbanColumn = 'new' | 'toCall' | 'called' | 'qualified';
// Export shared functions and data
export const callStatusOptions = [{
  value: 'nou',
  label: 'Nou',
  needsQualification: false
}, {
  value: 'de-apelat',
  label: 'De apelat',
  needsQualification: false
}, {
  value: 'nu-raspunde',
  label: '📞 Nu răspunde',
  needsQualification: false
}, {
  value: 'calificare',
  label: '✨ Calificare',
  needsQualification: true
}, {
  value: 'a-inchiriat',
  label: '⛔ A închiriat deja',
  needsQualification: false
}, {
  value: 'numar-invalid',
  label: '⛔ Număr invalid',
  needsQualification: false
}, {
  value: 'vrea-vizionare',
  label: '✅ Vrea vizionare',
  needsQualification: true
}, {
  value: 'nu-vrea-vizionare',
  label: '❌ Nu vrea vizionare - Calificare',
  needsQualification: true
}, {
  value: 'nu-colaboreaza',
  label: '🔄 Nu colaborează',
  needsQualification: false
}, {
  value: 'comision-redus',
  label: '💰 Oferă comision redus',
  needsQualification: false
}, {
  value: 'doreste-vanzare',
  label: '🏠 Dorește vânzare',
  needsQualification: false
}];
export const isInvalidStatus = (status: string) => {
  return ['a-inchiriat', 'numar-invalid'].includes(status);
};
export const needsQualification = (status: string) => {
  const option = callStatusOptions.find(opt => opt.value === status);
  return option?.needsQualification || false;
};
export const generateMockLeads = (): BuyerLead[] => {
  const today = new Date().toISOString().split('T')[0];
  const baseDate = new Date();
  baseDate.setHours(9, 0, 0, 0);
  return [{
    leadId: 'LI00001',
    createdAt: new Date(baseDate.getTime()).toISOString(),
    entryDate: today,
    entryTime: '09:15',
    name: 'Alexandru Popescu',
    phone: '0721111111',
    propertyId: 'P14501',
    callCenterMemo: 'Client interesat de apartament 2 camere în Titan, buget 450-500 EUR. A sunat pentru proprietatea P14501.',
    callCompleted: false,
    callStatus: 'nou',
    zones: [],
    qualified: false,
    actionHistory: [{
      action: 'Lead introdus în sistem',
      timestamp: `${today} 09:15`,
      agent: 'Call Center'
    }]
  }, {
    leadId: 'LI00002',
    createdAt: new Date(baseDate.getTime() + 15 * 60000).toISOString(),
    entryDate: today,
    entryTime: '09:30',
    name: 'Maria Ionescu',
    phone: '0732222222',
    callCenterMemo: 'Caută urgent apartament 3 camere în Drumul Taberei sau Militari. Buget flexibil până la 600 EUR.',
    callCompleted: false,
    callStatus: 'nou',
    zones: [],
    qualified: false,
    actionHistory: [{
      action: 'Lead introdus în sistem',
      timestamp: `${today} 09:30`,
      agent: 'Call Center'
    }]
  }, {
    leadId: 'LI00003',
    createdAt: new Date(baseDate.getTime() + 30 * 60000).toISOString(),
    entryDate: today,
    entryTime: '09:45',
    name: 'Andrei Popa',
    phone: '0743333333',
    propertyId: 'P14502',
    callCenterMemo: 'A sunat pentru proprietatea P14502 (garsonieră Pantelimon). Vrea să se mute în maxim 2 săptămâni.',
    callCompleted: false,
    callStatus: 'nou',
    zones: [],
    qualified: false,
    actionHistory: [{
      action: 'Lead introdus în sistem',
      timestamp: `${today} 09:45`,
      agent: 'Call Center'
    }]
  }, {
    leadId: 'LI00004',
    createdAt: new Date(baseDate.getTime() + 45 * 60000).toISOString(),
    entryDate: today,
    entryTime: '10:00',
    name: 'Elena Dumitrescu',
    phone: '0754444444',
    callCenterMemo: 'Caută apartament 2 camere în Colentina sau Obor. Buget 400-450 EUR. Preferă etaje intermediare.',
    callCompleted: false,
    callStatus: 'nou',
    zones: [],
    qualified: false,
    actionHistory: [{
      action: 'Lead introdus în sistem',
      timestamp: `${today} 10:00`,
      agent: 'Call Center'
    }]
  }, {
    leadId: 'LI00005',
    createdAt: new Date(baseDate.getTime() + 60 * 60000).toISOString(),
    entryDate: today,
    entryTime: '10:15',
    name: 'Cristian Marin',
    phone: '0765555555',
    propertyId: 'P14503',
    callCenterMemo: 'Interesat de proprietatea P14503 (3 camere Berceni). A întrebat despre posibilitatea de parcare.',
    callCompleted: false,
    callStatus: 'nou',
    zones: [],
    qualified: false,
    actionHistory: [{
      action: 'Lead introdus în sistem',
      timestamp: `${today} 10:15`,
      agent: 'Call Center'
    }]
  }, {
    leadId: 'LI00006',
    createdAt: new Date(baseDate.getTime() - 24 * 60 * 60000).toISOString(),
    entryDate: new Date(Date.now() - 24 * 60 * 60000).toISOString().split('T')[0],
    entryTime: '14:30',
    name: 'George Mihai',
    phone: '0721234567',
    callCenterMemo: 'Client serios, disponibil pentru vizionări weekend',
    callCompleted: true,
    callStatus: 'vrea-vizionare',
    zones: ['Titan', 'Colentina', 'Pantelimon'],
    fullName: 'George Mihai',
    propertyType: 'Apartament',
    rooms: 2,
    budgetMin: 400,
    budgetMax: 500,
    qualified: true,
    qualificationMemo: 'Client serios, disponibil weekend',
    actionHistory: [{
      action: 'Lead introdus în sistem',
      timestamp: new Date(Date.now() - 24 * 60 * 60000).toISOString().split('T')[0] + ' 14:30',
      agent: 'Call Center'
    }, {
      action: 'Lead preluat de agent',
      timestamp: new Date(Date.now() - 24 * 60 * 60000).toISOString().split('T')[0] + ' 15:00',
      agent: 'Agent Mihai'
    }, {
      action: 'Apel finalizat',
      timestamp: new Date(Date.now() - 24 * 60 * 60000).toISOString().split('T')[0] + ' 15:30',
      agent: 'Agent Mihai'
    }, {
      action: 'Status actualizat: Vrea vizionare',
      timestamp: new Date(Date.now() - 24 * 60 * 60000).toISOString().split('T')[0] + ' 15:35',
      agent: 'Agent Mihai'
    }, {
      action: 'Lead calificat',
      timestamp: new Date(Date.now() - 24 * 60 * 60000).toISOString().split('T')[0] + ' 16:00',
      agent: 'Agent Mihai'
    }]
  }, {
    leadId: 'LI00007',
    createdAt: new Date(baseDate.getTime() - 2 * 60 * 60000).toISOString(),
    entryDate: today,
    entryTime: '08:00',
    name: 'Laura Constantin',
    phone: '0732345678',
    callCenterMemo: 'Caută apartament 3 camere urgent',
    callCompleted: false,
    callStatus: 'nu-raspunde',
    zones: [],
    qualified: false,
    actionHistory: [{
      action: 'Lead introdus în sistem',
      timestamp: `${today} 08:00`,
      agent: 'Call Center'
    }, {
      action: 'Lead preluat de agent',
      timestamp: `${today} 09:00`,
      agent: 'Agent Ana'
    }, {
      action: 'Apel finalizat - Status: Nu răspunde (poate fi reapelat)',
      timestamp: `${today} 09:30`,
      agent: 'Agent Ana'
    }]
  }];
};
interface RentalLeadsKanbanProps {
  allLeads: BuyerLead[];
  onLeadUpdate: (leadId: string, updates: Partial<BuyerLead>) => void;
  onAddActionToHistory: (leadId: string, action: string, agent?: string) => void;
  searchQuery: string;
  filters: any;
}
// Helper function to determine if status is blocked
const isBlockedStatus = (status: string) => {
  return ['a-inchiriat', 'numar-invalid'].includes(status);
};
// Helper function to determine if status needs rescheduling - REMOVED comision-redus
const needsRescheduling = (status: string) => {
  return false; // Nu mai folosim reprogramare automată
};
// Helper function to determine if status needs redistribution to another agent
const needsRedistribution = (status: string) => {
  return status === 'nu-colaboreaza';
};
// Helper function to determine if status needs transform to sales
const needsTransform = (status: string) => {
  return status === 'doreste-vanzare';
};
export function RentalLeadsKanban({
  allLeads,
  onLeadUpdate,
  onAddActionToHistory,
  searchQuery,
  filters
}: RentalLeadsKanbanProps) {
  const navigate = useNavigate();
  const [selectedLead, setSelectedLead] = useState<BuyerLead | null>(null);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [isRescheduleModalOpen, setIsRescheduleModalOpen] = useState(false);
  const [isTransformModalOpen, setIsTransformModalOpen] = useState(false);
  const [leadForStatusUpdate, setLeadForStatusUpdate] = useState<BuyerLead | null>(null);
  const [leadForReschedule, setLeadForReschedule] = useState<BuyerLead | null>(null);
  const [leadForTransform, setLeadForTransform] = useState<BuyerLead | null>(null);
  const filteredLeads = useMemo(() => {
    return allLeads.filter(lead => {
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        if (!lead.leadId.toLowerCase().includes(query) && !lead.name.toLowerCase().includes(query) && !lead.phone.includes(query)) {
          return false;
        }
      }
      if (filters.leadId && !lead.leadId.toLowerCase().includes(filters.leadId.toLowerCase())) return false;
      if (filters.phone && !lead.phone.includes(filters.phone)) return false;
      if (filters.dateFrom && lead.entryDate < filters.dateFrom) return false;
      if (filters.dateTo && lead.entryDate > filters.dateTo) return false;
      if (filters.status && lead.callStatus !== filters.status) return false;
      if (filters.budgetMin && lead.budgetMin && lead.budgetMin < parseInt(filters.budgetMin)) return false;
      if (filters.budgetMax && lead.budgetMax && lead.budgetMax > parseInt(filters.budgetMax)) return false;
      return true;
    });
  }, [allLeads, searchQuery, filters]);
  const kanbanColumns = useMemo(() => {
    const columns: Record<KanbanColumn, BuyerLead[]> = {
      new: [],
      toCall: [],
      called: [],
      qualified: []
    };
    filteredLeads.forEach(lead => {
      // 4. CALIFICATE - doar leaduri cu qualified=true (vrea-vizionare sau nu-vrea-vizionare + formular completat)
      if (lead.qualified) {
        columns.qualified.push(lead);
      }
      // 2. DE APELAT - doar "de-apelat" și "nu-raspunde" (nu-raspunde rămâne aici)
      else if ((lead.callStatus === 'de-apelat' || lead.callStatus === 'nu-raspunde') && !lead.callCompleted) {
        columns.toCall.push(lead);
      }
      // 3. APELATE - toate statusurile care au callCompleted=true (exceptând qualified)
      else if (lead.callCompleted && !lead.qualified) {
        columns.called.push(lead);
      }
      // 1. LEAD NOU
      else if (lead.callStatus === 'nou') {
        columns.new.push(lead);
      }
    });
    return columns;
  }, [filteredLeads]);
  const stats = useMemo(() => {
    const totalLeads = filteredLeads.length;
    const qualified = filteredLeads.filter(l => l.qualified).length;
    const completedCalls = filteredLeads.filter(l => l.callCompleted).length;
    const progress = totalLeads > 0 ? completedCalls / totalLeads * 100 : 0;
    return {
      totalLeads,
      qualified,
      progress: progress.toFixed(0),
      new: kanbanColumns.new.length,
      toCall: kanbanColumns.toCall.length,
      called: kanbanColumns.called.length,
      qualifiedCount: kanbanColumns.qualified.length
    };
  }, [filteredLeads, kanbanColumns]);
  const handleCallFinalized = (lead: BuyerLead, e: React.MouseEvent) => {
    e.stopPropagation();
    setLeadForStatusUpdate(lead);
    setIsStatusModalOpen(true);
  };
  const handleStatusSubmit = (status: string, qualificationData?: Partial<BuyerLead>) => {
    if (!leadForStatusUpdate) return;
    // SPECIAL CASE: "Nu răspunde" - RĂMÂNE în "De Apelat"
    if (status === 'nu-raspunde') {
      onLeadUpdate(leadForStatusUpdate.leadId, {
        callStatus: status,
        callCompleted: false // IMPORTANT: false pentru a rămâne în "De Apelat"
      });
      onAddActionToHistory(leadForStatusUpdate.leadId, 'Apel finalizat - Status: Nu răspunde (poate fi reapelat)');
      // Update selectedLead if it's the same lead
      if (selectedLead?.leadId === leadForStatusUpdate.leadId) {
        setSelectedLead({
          ...selectedLead,
          callStatus: status,
          callCompleted: false
        });
      }
      setIsStatusModalOpen(false);
      setLeadForStatusUpdate(null);
      return;
    }
    // TOATE CELELALTE STATUSURI - Trec în "Apelate" (callCompleted: true)
    const updates: Partial<BuyerLead> = {
      callCompleted: true,
      callStatus: status
    };
    // Dacă statusul necesită calificare (vrea-vizionare sau nu-vrea-vizionare)
    if (needsQualification(status)) {
      // Dacă avem date de calificare, marcăm ca qualified
      if (qualificationData) {
        Object.assign(updates, qualificationData, {
          qualified: true
        });
      }
      // Altfel, doar setăm statusul și așteptăm calificarea ulterioară
    }
    onLeadUpdate(leadForStatusUpdate.leadId, updates);
    const statusLabel = callStatusOptions.find(o => o.value === status)?.label || status;
    onAddActionToHistory(leadForStatusUpdate.leadId, `Apel finalizat - Status: ${statusLabel}`);
    if (qualificationData) {
      onAddActionToHistory(leadForStatusUpdate.leadId, 'Lead calificat');
    }
    // Update selectedLead if it's the same lead
    if (selectedLead?.leadId === leadForStatusUpdate.leadId) {
      setSelectedLead({
        ...selectedLead,
        ...updates
      });
    }
    setIsStatusModalOpen(false);
    setLeadForStatusUpdate(null);
  };
  const handleRescheduleCall = (lead: BuyerLead, e: React.MouseEvent) => {
    e.stopPropagation();
    setLeadForReschedule(lead);
    setIsRescheduleModalOpen(true);
  };
  const handleRescheduleSubmit = (date: string, time: string, notes: string) => {
    if (!leadForReschedule) return;
    onLeadUpdate(leadForReschedule.leadId, {
      scheduledCallDate: date,
      scheduledCallTime: time,
      rescheduleNotes: notes
    });
    onAddActionToHistory(leadForReschedule.leadId, `Apel reprogramat automat pentru ${date} la ${time}`);
    setIsRescheduleModalOpen(false);
    setLeadForReschedule(null);
    alert('✅ Apel reprogramat automat cu succes! Vei primi notificare în ziua programată.');
  };
  const handleTransformLead = (lead: BuyerLead, e: React.MouseEvent) => {
    e.stopPropagation();
    setLeadForTransform(lead);
    setIsTransformModalOpen(true);
  };
  const handleTransformSubmit = (segment: 'vanzare' | 'inchiriere', leadData: {
    name: string;
    phone: string;
    email?: string;
    notes: string;
  }) => {
    if (!leadForTransform) return;
    // Mark original lead as transformed
    onLeadUpdate(leadForTransform.leadId, {
      callStatus: 'transformat',
      callCompleted: true
    });
    onAddActionToHistory(leadForTransform.leadId, `Lead transformat în segment: ${segment === 'vanzare' ? 'Vânzare' : 'Închiriere'}`);
    // Create new lead (in real implementation, this would create a new lead in the system)
    const newLeadMessage = `✅ Lead transformat cu succes!\n\nLead nou creat în segmentul: ${segment === 'vanzare' ? 'Vânzare' : 'Închiriere'}\nNume: ${leadData.name}\nTelefon: ${leadData.phone}\n${leadData.email ? `Email: ${leadData.email}\n` : ''}${leadData.notes ? `Notițe: ${leadData.notes}` : ''}`;
    alert(newLeadMessage);
    setIsTransformModalOpen(false);
    setLeadForTransform(null);
  };
  const handleEditRequest = (lead: BuyerLead, e: React.MouseEvent) => {
    e.stopPropagation();
    onAddActionToHistory(lead.leadId, 'Cerere editată - Transfer la închirieri');
    alert('📝 Lead transferat la secțiunea de închirieri pentru editare cerere');
  };
  const handleQualificationSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedLead) return;
    if (!selectedLead.fullName || !selectedLead.propertyType || !selectedLead.rooms || !selectedLead.budgetMin || !selectedLead.budgetMax || selectedLead.zones.length < 3) {
      alert('Te rog completează toate câmpurile obligatorii (inclusiv minim 3 zone)');
      return;
    }
    onLeadUpdate(selectedLead.leadId, {
      qualified: true
    });
    onAddActionToHistory(selectedLead.leadId, 'Lead calificat');
    setSelectedLead(null);
    alert('✅ Lead calificat cu succes!');
  };
  const getStatusColor = (status: string) => {
    if (isBlockedStatus(status)) return 'bg-red-500';
    if (needsQualification(status)) return 'bg-[#21A366]';
    if (needsRedistribution(status)) return 'bg-orange-500';
    if (needsTransform(status)) return 'bg-blue-500';
    if (status === 'comision-redus') return 'bg-yellow-500';
    switch (status) {
      case 'nu-raspunde':
        return 'bg-amber-500';
      default:
        return 'bg-gray-500';
    }
  };
  const handleLeadClick = (lead: BuyerLead) => {
    // If lead is qualified, show enhanced panel
    if (lead.qualified) {
      setSelectedLead(lead);
    } else {
      // Show regular panel for non-qualified leads
      setSelectedLead(lead);
    }
  };
  // Helper function to get the correct action button based on status
  const getActionButton = (lead: BuyerLead) => {
    // Blocked statuses (a-inchiriat, numar-invalid) - show only details button
    if (isBlockedStatus(lead.callStatus)) {
      return <div className="mt-3 pt-3 border-t border-gray-100 space-y-2" data-id="element-10949">
          <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-center" data-id="element-10950">
            <p className="text-xs text-red-600 font-bold" data-id="element-10951">🔒 Lead Blocat</p>
          </div>
          <button onClick={e => {
          e.stopPropagation();
          setSelectedLead(lead);
        }} className="w-full bg-gray-100 text-gray-700 px-3 py-2 rounded-lg text-xs font-semibold hover:bg-gray-200 transition-colors" data-id="element-10952">
            👁️ Detalii
          </button>
        </div>;
    }
    // Needs qualification (vrea-vizionare, nu-vrea-vizionare, calificare) - show qualification + details
    if (needsQualification(lead.callStatus) && !lead.qualified) {
      return <div className="mt-3 pt-3 border-t border-gray-100" data-id="element-10953">
          <div className="grid grid-cols-2 gap-2" data-id="element-10954">
            <button onClick={e => {
            e.stopPropagation();
            setSelectedLead(lead);
          }} className="bg-green-500 text-white px-3 py-2 rounded-lg text-xs font-semibold hover:bg-green-600 transition-colors flex items-center justify-center space-x-1" data-id="element-10955">
              <CheckCircleIcon className="w-3 h-3" data-id="element-10956" />
              <span data-id="element-10957">Califică</span>
            </button>
            <button onClick={e => {
            e.stopPropagation();
            setSelectedLead(lead);
          }} className="bg-gray-100 text-gray-700 px-3 py-2 rounded-lg text-xs font-semibold hover:bg-gray-200 transition-colors" data-id="element-10958">
              👁️ Detalii
            </button>
          </div>
        </div>;
    }
    // "Nu colaborează" și "Oferă comision redus" - Reapelare + Detalii
    if (lead.callStatus === 'nu-colaboreaza' || lead.callStatus === 'comision-redus') {
      return <div className="mt-3 pt-3 border-t border-gray-100" data-id="element-10959">
          <div className="grid grid-cols-2 gap-2" data-id="element-10960">
            <button onClick={e => handleCallFinalized(lead, e)} className="bg-amber-500 text-white px-3 py-2 rounded-lg text-xs font-semibold hover:bg-amber-600 transition-colors flex items-center justify-center space-x-1" data-id="element-10961">
              <PhoneIcon className="w-3 h-3" data-id="element-10962" />
              <span data-id="element-10963">Reapelare</span>
            </button>
            <button onClick={e => {
            e.stopPropagation();
            setSelectedLead(lead);
          }} className="bg-gray-100 text-gray-700 px-3 py-2 rounded-lg text-xs font-semibold hover:bg-gray-200 transition-colors" data-id="element-10964">
              👁️ Detalii
            </button>
          </div>
        </div>;
    }
    // "Dorește vânzare" - Transformă + Detalii
    if (needsTransform(lead.callStatus)) {
      return <div className="mt-3 pt-3 border-t border-gray-100" data-id="element-10965">
          <div className="grid grid-cols-2 gap-2" data-id="element-10966">
            <button onClick={e => handleTransformLead(lead, e)} className="bg-blue-500 text-white px-3 py-2 rounded-lg text-xs font-semibold hover:bg-blue-600 transition-colors flex items-center justify-center space-x-1" data-id="element-10967">
              <RefreshCwIcon className="w-3 h-3" data-id="element-10968" />
              <span data-id="element-10969">Transformă</span>
            </button>
            <button onClick={e => {
            e.stopPropagation();
            setSelectedLead(lead);
          }} className="bg-gray-100 text-gray-700 px-3 py-2 rounded-lg text-xs font-semibold hover:bg-gray-200 transition-colors" data-id="element-10970">
              👁️ Detalii
            </button>
          </div>
        </div>;
    }
    // Default - only details button for other statuses
    return <div className="mt-3 pt-3 border-t border-gray-100" data-id="element-10971">
        <button onClick={e => {
        e.stopPropagation();
        setSelectedLead(lead);
      }} className="w-full bg-gray-100 text-gray-700 px-3 py-2 rounded-lg text-xs font-semibold hover:bg-gray-200 transition-colors" data-id="element-10972">
          👁️ Detalii
        </button>
      </div>;
  };
  return <div className="w-full min-h-screen bg-gradient-to-br from-gray-50 to-gray-100" data-id="element-10973">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-30 shadow-sm" data-id="element-10974">
        <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-4" data-id="element-10975">
          <div className="flex items-center justify-between" data-id="element-10976">
            <div data-id="element-10977">
              <div className="flex items-center space-x-3" data-id="element-10978">
                <h1 className="text-2xl font-bold text-[#132C53]" data-id="element-10979">
                  👥 Kanban Leaduri Cumpărători
                </h1>
                <button onClick={() => navigate('/inchiriere/leaduri')} className="flex items-center space-x-2 bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1.5 rounded-lg text-sm font-semibold transition-all" data-id="element-10980">
                  <ListIcon className="w-4 h-4" data-id="element-10981" />
                  <span data-id="element-10982">List View</span>
                </button>
              </div>
              <p className="text-sm text-gray-600 mt-1" data-id="element-10983">
                {stats.totalLeads} leaduri • {stats.qualified} calificate •{' '}
                {stats.progress}% progres
              </p>
            </div>
            <div className="hidden md:flex items-center space-x-4" data-id="element-10984">
              <div className="w-48" data-id="element-10985">
                <div className="flex items-center justify-between text-xs text-gray-600 mb-1" data-id="element-10986">
                  <span data-id="element-10987">Progres apeluri</span>
                  <span className="font-bold" data-id="element-10988">{stats.progress}%</span>
                </div>
                <div className="bg-gray-200 rounded-full h-2 overflow-hidden" data-id="element-10989">
                  <motion.div initial={{
                  width: 0
                }} animate={{
                  width: `${stats.progress}%`
                }} transition={{
                  duration: 1,
                  ease: 'easeOut'
                }} className="bg-gradient-to-r from-[#357BEB] to-[#21A366] h-full rounded-full" data-id="element-10990" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Kanban Board */}
      <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-6" data-id="element-10991">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-10992">
          {/* Column 1: Lead Nou */}
          <div className="bg-white rounded-xl shadow-sm border-2 border-gray-200" data-id="element-10993">
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-4 py-3 rounded-t-xl" data-id="element-10994">
              <div className="flex items-center justify-between" data-id="element-10995">
                <div className="flex items-center space-x-2" data-id="element-10996">
                  <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center" data-id="element-10997">
                    <UserIcon className="w-4 h-4" data-id="element-10998" />
                  </div>
                  <div data-id="element-10999">
                    <h3 className="font-bold text-sm" data-id="element-11000">Lead Nou</h3>
                    <p className="text-xs text-blue-100" data-id="element-11001">De preluat</p>
                  </div>
                </div>
                <span className="px-2 py-1 bg-white bg-opacity-20 rounded-full text-xs font-bold" data-id="element-11002">
                  {stats.new}
                </span>
              </div>
            </div>

            <div className="p-3 space-y-3 max-h-[calc(100vh-280px)] overflow-y-auto" data-id="element-11003">
              {kanbanColumns.new.map((lead, index) => <motion.div key={lead.leadId} initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: index * 0.05
            }} className="bg-white border-2 border-gray-200 rounded-lg p-3 hover:border-blue-500 hover:shadow-md transition-all cursor-pointer group" onClick={() => handleLeadClick(lead)} data-id="element-11004">
                  <div className="flex items-start justify-between mb-2" data-id="element-11005">
                    <div className="flex items-center space-x-2" data-id="element-11006">
                      <GripVerticalIcon className="w-4 h-4 text-gray-300 group-hover:text-blue-500" data-id="element-11007" />
                      <span className="text-xs font-mono font-bold text-blue-600" data-id="element-11008">
                        {lead.leadId}
                      </span>
                    </div>
                    <div className="text-right" data-id="element-11009">
                      <p className="text-xs text-gray-500" data-id="element-11010">{lead.entryTime}</p>
                    </div>
                  </div>

                  <h4 className="font-bold text-gray-900 mb-1" data-id="element-11011">{lead.name}</h4>

                  <div className="flex items-center space-x-2 text-xs text-gray-600 mb-2" data-id="element-11012">
                    <PhoneIcon className="w-3 h-3" data-id="element-11013" />
                    <span className="font-medium" data-id="element-11014">{lead.phone}</span>
                  </div>

                  {lead.propertyId && <div className="flex items-center space-x-2 text-xs text-[#357BEB] mb-2" data-id="element-11015">
                      <HomeIcon className="w-3 h-3" data-id="element-11016" />
                      <span className="font-semibold" data-id="element-11017">{lead.propertyId}</span>
                    </div>}

                  <div className="bg-gray-50 rounded px-2 py-1 mb-3" data-id="element-11018">
                    <p className="text-xs text-gray-600 line-clamp-2" data-id="element-11019">
                      {lead.callCenterMemo}
                    </p>
                  </div>

                  <div className="mt-3 pt-3 border-t border-gray-100" data-id="element-11020">
                    <div className="grid grid-cols-2 gap-2" data-id="element-11021">
                      <button onClick={e => {
                    e.stopPropagation();
                    onLeadUpdate(lead.leadId, {
                      callStatus: 'de-apelat'
                    });
                    onAddActionToHistory(lead.leadId, 'Lead preluat de agent');
                    setSelectedLead(null);
                  }} className="bg-blue-500 text-white px-3 py-2 rounded-lg text-xs font-semibold hover:bg-blue-600 transition-colors" data-id="element-11022">
                        ✓ Preluare
                      </button>
                      <button onClick={e => {
                    e.stopPropagation();
                    setSelectedLead(lead);
                  }} className="bg-gray-100 text-gray-700 px-3 py-2 rounded-lg text-xs font-semibold hover:bg-gray-200 transition-colors" data-id="element-11023">
                        👁️ Detalii
                      </button>
                    </div>
                  </div>
                </motion.div>)}

              {kanbanColumns.new.length === 0 && <div className="text-center py-8 text-gray-400" data-id="element-11024">
                  <CheckCircleIcon className="w-12 h-12 mx-auto mb-2 opacity-50" data-id="element-11025" />
                  <p className="text-sm" data-id="element-11026">Toate preluate!</p>
                </div>}
            </div>
          </div>

          {/* Column 2: De Apelat */}
          <div className="bg-white rounded-xl shadow-sm border-2 border-gray-200" data-id="element-11027">
            <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white px-4 py-3 rounded-t-xl" data-id="element-11028">
              <div className="flex items-center justify-between" data-id="element-11029">
                <div className="flex items-center space-x-2" data-id="element-11030">
                  <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center" data-id="element-11031">
                    <PhoneIcon className="w-4 h-4" data-id="element-11032" />
                  </div>
                  <div data-id="element-11033">
                    <h3 className="font-bold text-sm" data-id="element-11034">De Apelat</h3>
                    <p className="text-xs text-purple-100" data-id="element-11035">Efectuează apelul</p>
                  </div>
                </div>
                <span className="px-2 py-1 bg-white bg-opacity-20 rounded-full text-xs font-bold" data-id="element-11036">
                  {stats.toCall}
                </span>
              </div>
            </div>

            <div className="p-3 space-y-3 max-h-[calc(100vh-280px)] overflow-y-auto" data-id="element-11037">
              {kanbanColumns.toCall.map((lead, index) => <motion.div key={lead.leadId} initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: index * 0.05
            }} className="bg-white border-2 border-gray-200 rounded-lg p-3 hover:border-purple-500 hover:shadow-md transition-all cursor-pointer group" onClick={() => handleLeadClick(lead)} data-id="element-11038">
                  <div className="flex items-start justify-between mb-2" data-id="element-11039">
                    <div className="flex items-center space-x-2" data-id="element-11040">
                      <GripVerticalIcon className="w-4 h-4 text-gray-300 group-hover:text-purple-500" data-id="element-11041" />
                      <span className="text-xs font-mono font-bold text-gray-500" data-id="element-11042">
                        {lead.leadId}
                      </span>
                    </div>
                  </div>

                  <h4 className="font-bold text-gray-900 mb-1" data-id="element-11043">{lead.name}</h4>

                  <a href={`tel:${lead.phone}`} onClick={e => e.stopPropagation()} className="flex items-center space-x-2 text-sm text-purple-600 font-bold hover:underline mb-3" data-id="element-11044">
                    <PhoneIcon className="w-4 h-4" data-id="element-11045" />
                    <span data-id="element-11046">{lead.phone}</span>
                  </a>

                  {lead.propertyId && <div className="flex items-center space-x-2 text-xs text-[#357BEB] mb-2" data-id="element-11047">
                      <HomeIcon className="w-3 h-3" data-id="element-11048" />
                      <span className="font-semibold" data-id="element-11049">{lead.propertyId}</span>
                    </div>}

                  <div className="bg-gray-50 rounded px-2 py-1 mb-3" data-id="element-11050">
                    <p className="text-xs text-gray-600 line-clamp-2" data-id="element-11051">
                      {lead.callCenterMemo}
                    </p>
                  </div>

                  <div className="mt-3 pt-3 border-t border-gray-100" data-id="element-11052">
                    {lead.callStatus === 'nu-raspunde' ? <div className="grid grid-cols-2 gap-2" data-id="element-11053">
                        <button onClick={e => handleCallFinalized(lead, e)} className="bg-amber-500 text-white px-3 py-2 rounded-lg text-xs font-semibold hover:bg-amber-600 transition-colors flex items-center justify-center space-x-1" data-id="element-11054">
                          <PhoneIcon className="w-3 h-3" data-id="element-11055" />
                          <span data-id="element-11056">Reapelare</span>
                        </button>
                        <button onClick={e => {
                    e.stopPropagation();
                    setSelectedLead(lead);
                  }} className="bg-gray-100 text-gray-700 px-3 py-2 rounded-lg text-xs font-semibold hover:bg-gray-200 transition-colors" data-id="element-11057">
                          👁️ Detalii
                        </button>
                      </div> : <div className="grid grid-cols-2 gap-2" data-id="element-11058">
                        <button onClick={e => handleCallFinalized(lead, e)} className="bg-purple-500 text-white px-3 py-2 rounded-lg text-xs font-semibold hover:bg-purple-600 transition-colors" data-id="element-11059">
                          ✓ Finalizat
                        </button>
                        <button onClick={e => {
                    e.stopPropagation();
                    setSelectedLead(lead);
                  }} className="bg-gray-100 text-gray-700 px-3 py-2 rounded-lg text-xs font-semibold hover:bg-gray-200 transition-colors" data-id="element-11060">
                          👁️ Detalii
                        </button>
                      </div>}
                  </div>
                </motion.div>)}

              {kanbanColumns.toCall.length === 0 && <div className="text-center py-8 text-gray-400" data-id="element-11061">
                  <PhoneIcon className="w-12 h-12 mx-auto mb-2 opacity-50" data-id="element-11062" />
                  <p className="text-sm" data-id="element-11063">Niciun apel de făcut</p>
                </div>}
            </div>
          </div>

          {/* Column 3: Apelate */}
          <div className="bg-white rounded-xl shadow-sm border-2 border-gray-200" data-id="element-11064">
            <div className="bg-gradient-to-r from-amber-500 to-amber-600 text-white px-4 py-3 rounded-t-xl" data-id="element-11065">
              <div className="flex items-center justify-between" data-id="element-11066">
                <div className="flex items-center space-x-2" data-id="element-11067">
                  <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center" data-id="element-11068">
                    <EditIcon className="w-4 h-4" data-id="element-11069" />
                  </div>
                  <div data-id="element-11070">
                    <h3 className="font-bold text-sm" data-id="element-11071">Apelate</h3>
                    <p className="text-xs text-amber-100" data-id="element-11072">Acțiuni necesare</p>
                  </div>
                </div>
                <span className="px-2 py-1 bg-white bg-opacity-20 rounded-full text-xs font-bold" data-id="element-11073">
                  {stats.called}
                </span>
              </div>
            </div>

            <div className="p-3 space-y-3 max-h-[calc(100vh-280px)] overflow-y-auto" data-id="element-11074">
              {kanbanColumns.called.map((lead, index) => <motion.div key={lead.leadId} initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: index * 0.05
            }} className={`bg-white rounded-lg p-3 hover:shadow-md transition-all cursor-pointer group ${isBlockedStatus(lead.callStatus) ? 'border-2 border-red-500 opacity-75' : 'border-2 border-gray-200 hover:border-amber-500'}`} onClick={() => handleLeadClick(lead)} data-id="element-11075">
                  <div className="flex items-start justify-between mb-2" data-id="element-11076">
                    <div className="flex items-center space-x-2" data-id="element-11077">
                      <GripVerticalIcon className={`w-4 h-4 ${isBlockedStatus(lead.callStatus) ? 'text-red-500' : 'text-gray-300 group-hover:text-amber-500'}`} data-id="element-11078" />
                      <span className="text-xs font-mono font-bold text-gray-500" data-id="element-11079">
                        {lead.leadId}
                      </span>
                    </div>
                    <span className={`px-2 py-1 ${getStatusColor(lead.callStatus)} text-white rounded-full text-xs font-bold`} data-id="element-11080">
                      {isBlockedStatus(lead.callStatus) ? '🔒' : lead.callStatus === 'nu-raspunde' ? '📞' : needsRescheduling(lead.callStatus) ? '🔄' : needsTransform(lead.callStatus) ? '📝' : '✗'}
                    </span>
                  </div>

                  <h4 className="font-bold text-gray-900 mb-1" data-id="element-11081">{lead.name}</h4>

                  <div className="flex items-center space-x-2 text-xs text-gray-600 mb-2" data-id="element-11082">
                    <PhoneIcon className="w-3 h-3" data-id="element-11083" />
                    <span className="font-medium" data-id="element-11084">{lead.phone}</span>
                  </div>

                  {lead.propertyId && <div className="flex items-center space-x-2 text-xs text-[#357BEB] mb-2" data-id="element-11085">
                      <HomeIcon className="w-3 h-3" data-id="element-11086" />
                      <span className="font-semibold" data-id="element-11087">{lead.propertyId}</span>
                    </div>}

                  {isBlockedStatus(lead.callStatus) && <div className="bg-red-50 border border-red-200 rounded px-2 py-1 mb-2" data-id="element-11088">
                      <p className="text-xs text-red-600 font-semibold" data-id="element-11089">
                        🔒 Lead blocat permanent
                      </p>
                    </div>}

                  {lead.callStatus === 'nu-raspunde' && <div className="bg-orange-50 border border-orange-200 rounded px-2 py-1 mb-2" data-id="element-11090">
                      <p className="text-xs text-orange-600 font-semibold" data-id="element-11091">
                        📞 Reîncearcă apelul
                      </p>
                    </div>}

                  {needsRescheduling(lead.callStatus) && <div className="bg-orange-50 border border-orange-200 rounded px-2 py-1 mb-2" data-id="element-11092">
                      <p className="text-xs text-orange-600 font-semibold" data-id="element-11093">
                        🔄 Necesită reprogramare
                      </p>
                    </div>}

                  {needsTransform(lead.callStatus) && <div className="bg-blue-50 border border-blue-200 rounded px-2 py-1 mb-2" data-id="element-11094">
                      <p className="text-xs text-blue-600 font-semibold" data-id="element-11095">
                        📝 Editează cererea
                      </p>
                    </div>}

                  <div className="bg-gray-50 rounded px-2 py-1 mb-2" data-id="element-11096">
                    <p className="text-xs text-gray-600 line-clamp-2" data-id="element-11097">
                      {lead.callCenterMemo}
                    </p>
                  </div>

                  {/* Dynamic action button based on status */}
                  {getActionButton(lead)}
                </motion.div>)}

              {kanbanColumns.called.length === 0 && <div className="text-center py-8 text-gray-400" data-id="element-11098">
                  <EditIcon className="w-12 h-12 mx-auto mb-2 opacity-50" data-id="element-11099" />
                  <p className="text-sm" data-id="element-11100">Niciun lead apelat</p>
                </div>}
            </div>
          </div>

          {/* Column 4: Calificare */}
          <div className="bg-white rounded-xl shadow-sm border-2 border-gray-200" data-id="element-11101">
            <div className="bg-gradient-to-r from-[#21A366] to-[#1a8555] text-white px-4 py-3 rounded-t-xl" data-id="element-11102">
              <div className="flex items-center justify-between" data-id="element-11103">
                <div className="flex items-center space-x-2" data-id="element-11104">
                  <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center" data-id="element-11105">
                    <CheckCircleIcon className="w-4 h-4" data-id="element-11106" />
                  </div>
                  <div data-id="element-11107">
                    <h3 className="font-bold text-sm" data-id="element-11108">Calificare</h3>
                    <p className="text-xs text-green-100" data-id="element-11109">Leaduri calificate</p>
                  </div>
                </div>
                <span className="px-2 py-1 bg-white bg-opacity-20 rounded-full text-xs font-bold" data-id="element-11110">
                  {stats.qualifiedCount}
                </span>
              </div>
            </div>

            <div className="p-3 space-y-3 max-h-[calc(100vh-280px)] overflow-y-auto" data-id="element-11111">
              {kanbanColumns.qualified.map((lead, index) => <motion.div key={lead.leadId} initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              delay: index * 0.05
            }} className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-[#21A366] rounded-lg p-3 hover:shadow-md transition-all cursor-pointer" onClick={() => handleLeadClick(lead)} data-id="element-11112">
                  <div className="flex items-start justify-between mb-2" data-id="element-11113">
                    <span className="text-xs font-mono font-bold text-[#21A366]" data-id="element-11114">
                      {lead.leadId}
                    </span>
                    <CheckCircleIcon className="w-4 h-4 text-[#21A366]" data-id="element-11115" />
                  </div>

                  <h4 className="font-bold text-gray-900 mb-1" data-id="element-11116">{lead.name}</h4>

                  <div className="flex items-center space-x-2 text-xs text-gray-600 mb-2" data-id="element-11117">
                    <PhoneIcon className="w-3 h-3" data-id="element-11118" />
                    <span className="font-medium" data-id="element-11119">{lead.phone}</span>
                  </div>

                  {lead.qualified && lead.rooms && <div className="bg-white rounded-lg px-2 py-1 mt-2 space-y-1" data-id="element-11120">
                      <div className="flex items-center justify-between text-xs" data-id="element-11121">
                        <span className="text-gray-500" data-id="element-11122">Tip:</span>
                        <span className="font-semibold text-gray-900" data-id="element-11123">
                          {lead.propertyType}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-xs" data-id="element-11124">
                        <span className="text-gray-500" data-id="element-11125">Camere:</span>
                        <span className="font-semibold text-gray-900" data-id="element-11126">
                          {lead.rooms}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-xs" data-id="element-11127">
                        <span className="text-gray-500" data-id="element-11128">Buget:</span>
                        <span className="font-semibold text-[#21A366]" data-id="element-11129">
                          {lead.budgetMin}-{lead.budgetMax} EUR
                        </span>
                      </div>
                    </div>}

                  {!lead.qualified && <div className="bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-2" data-id="element-11130">
                      <p className="text-xs text-amber-700 font-semibold" data-id="element-11131">
                        ⚠️ Necesită calificare
                      </p>
                    </div>}
                </motion.div>)}

              {kanbanColumns.qualified.length === 0 && <div className="text-center py-8 text-gray-400" data-id="element-11132">
                  <CheckCircleIcon className="w-12 h-12 mx-auto mb-2 opacity-50" data-id="element-11133" />
                  <p className="text-sm" data-id="element-11134">Niciun lead calificat</p>
                </div>}
            </div>
          </div>
        </div>
      </div>

      {/* Call Status Modal */}
      <CallStatusModal isOpen={isStatusModalOpen} onClose={() => {
      setIsStatusModalOpen(false);
      setLeadForStatusUpdate(null);
    }} onSubmit={handleStatusSubmit} onRescheduleCall={status => {
      if (leadForStatusUpdate) {
        // Update status first, then open reschedule modal
        onLeadUpdate(leadForStatusUpdate.leadId, {
          callStatus: status,
          callCompleted: true
        });
        setLeadForReschedule(leadForStatusUpdate);
        setIsRescheduleModalOpen(true);
        setIsStatusModalOpen(false);
      }
    }} onEditRequest={() => {
      if (leadForStatusUpdate) {
        // Update status first, then open transform modal
        onLeadUpdate(leadForStatusUpdate.leadId, {
          callStatus: 'doreste-vanzare',
          callCompleted: true
        });
        setLeadForTransform(leadForStatusUpdate);
        setIsTransformModalOpen(true);
        setIsStatusModalOpen(false);
      }
    }} leadName={leadForStatusUpdate?.name || ''} currentLead={leadForStatusUpdate || undefined} data-id="element-11135" />

      {/* Reschedule Call Modal */}
      <RescheduleCallModal isOpen={isRescheduleModalOpen} onClose={() => {
      setIsRescheduleModalOpen(false);
      setLeadForReschedule(null);
    }} onSubmit={handleRescheduleSubmit} leadName={leadForReschedule?.name || ''} currentStatus={leadForReschedule?.callStatus || ''} data-id="element-11136" />

      {/* Transform Lead Modal */}
      {leadForTransform && <TransformLeadModal isOpen={isTransformModalOpen} onClose={() => {
      setIsTransformModalOpen(false);
      setLeadForTransform(null);
    }} onSubmit={handleTransformSubmit} currentLead={{
      leadId: leadForTransform.leadId,
      name: leadForTransform.name,
      phone: leadForTransform.phone
    }} data-id="element-11137" />}

      {/* Side Panel */}
      <AnimatePresence data-id="element-11138">
        {selectedLead && <>
            {selectedLead.qualified ? <QualifiedLeadDetailsPanel lead={selectedLead} onClose={() => setSelectedLead(null)} data-id="element-11139" /> : <LeadDetailsPanel lead={selectedLead} onClose={() => setSelectedLead(null)} onUpdateStatus={() => {
          setLeadForStatusUpdate(selectedLead);
          setIsStatusModalOpen(true);
        }} onLeadUpdate={(leadId, updates) => {
          onLeadUpdate(leadId, updates);
          // Update selectedLead to reflect changes
          setSelectedLead({
            ...selectedLead,
            ...updates
          });
        }} data-id="element-11140" />}
          </>}
      </AnimatePresence>
    </div>;
}